/**
 * V11.9 — Admin : paramétrage global du site.
 * - Date JP2027 (compteur J-N)
 * - Bannière visuelle Mémoire + bibliothèque de livres
 * Rôle admin/COPF uniquement.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Flag, BookOpen, Save, Plus, Trash2, Image as ImageIcon, ExternalLink, Library, Settings, Mail,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import { showError } from "@/lib/errors";

export default function AdminSiteSettingsPage() {
  const { user, loading: authLoading } = useAuth();
  const isStaff = (user?.roles || []).some((r) => ["admin", "copf"].includes(r));

  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("jp2027");

  useEffect(() => {
    // V13.13 — On attend que l'auth soit chargée pour éviter un flash "Accès
    // réservé" pendant la 1ère requête /auth/me. On ne déclenche l'appel
    // /site-settings que si l'utilisateur est staff.
    if (authLoading) return;
    if (!isStaff) { setLoading(false); return; }
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    api.get("/site-settings")
      .then((r) => { if (!cancelled) setSettings(r.data); })
      .catch((e) => {
        if (cancelled) return;
        setLoadError(e?.response?.data?.detail || e?.message || "Erreur de chargement des paramètres.");
        showError(e);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [authLoading, isStaff]);

  // Attente de l'auth : on affiche un skeleton neutre plutôt que "Accès réservé"
  if (authLoading) {
    return <div className="mx-auto max-w-4xl px-4 py-8"><Skeleton className="h-96 w-full" /></div>;
  }
  if (!isStaff) {
    return <div className="mx-auto max-w-3xl px-4 py-16 text-center" data-testid="site-settings-forbidden">
      <Settings className="h-10 w-10 mx-auto text-muted-foreground mb-2" />
      <p>Accès réservé aux rôles Admin/COPF.</p>
      {user && <p className="text-xs text-muted-foreground mt-2">Rôles actuels : {(user.roles || []).join(", ") || "aucun"}</p>}
    </div>;
  }
  if (loading) {
    return <div className="mx-auto max-w-4xl px-4 py-8"><Skeleton className="h-96 w-full" /></div>;
  }
  if (loadError || !settings) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-12 text-center space-y-3" data-testid="site-settings-error">
        <Settings className="h-10 w-10 mx-auto text-orange-500 mb-1" />
        <p className="text-base font-medium">Impossible de charger les paramètres du site</p>
        <p className="text-sm text-muted-foreground">{loadError || "Réponse inattendue du serveur."}</p>
        <Button variant="outline" onClick={() => window.location.reload()} data-testid="site-settings-retry">Réessayer</Button>
      </div>
    );
  }

  // Countdown live preview
  const openingDate = settings.jp2027?.opening_date || "2027-07-14";
  const daysLeft = Math.max(0, Math.ceil((new Date(`${openingDate}T00:00:00`) - new Date()) / (86400000)));

  const updJp = (patch) => setSettings((s) => ({ ...s, jp2027: { ...s.jp2027, ...patch } }));
  const updMem = (patch) => setSettings((s) => ({ ...s, memoire: { ...s.memoire, ...patch } }));

  const updBook = (i, patch) => {
    setSettings((s) => {
      const books = [...(s.memoire?.books || [])];
      books[i] = { ...books[i], ...patch };
      return { ...s, memoire: { ...s.memoire, books } };
    });
  };
  const addBook = () => {
    setSettings((s) => ({
      ...s, memoire: {
        ...s.memoire,
        books: [...(s.memoire?.books || []), { title: "", author: "", year: "", cover_url: "", external_url: "", description: "" }],
      },
    }));
  };
  const removeBook = (i) => {
    setSettings((s) => {
      const books = [...(s.memoire?.books || [])];
      books.splice(i, 1);
      return { ...s, memoire: { ...s.memoire, books } };
    });
  };

  const save = async (section) => {
    setSaving(true);
    try {
      let payload;
      if (section === "jp2027") payload = { jp2027: settings.jp2027 };
      else if (section === "admin_contact") payload = { admin_contact: settings.admin_contact };
      else payload = { memoire: settings.memoire };
      const { data } = await api.patch("/admin/site-settings", payload);
      setSettings(data);
      const label = { jp2027: "Paramètres JP 2027", memoire: "Paramètres Mémoire", admin_contact: "Email admin" }[section];
      toast.success(`${label} enregistré${section === "admin_contact" ? "" : "s"}.`);
      // Rafraîchit le cache mailto côté client
      if (section === "admin_contact") {
        import("@/lib/adminContact").then((m) => m.refreshAdminEmail?.());
      }
    } catch (e) { showError(e); } finally { setSaving(false); }
  };
  const updAdmin = (patch) => setSettings((s) => ({ ...s, admin_contact: { ...(s.admin_contact || {}), ...patch } }));

  return (
    <div className="mx-auto w-full max-w-5xl px-4 sm:px-6 lg:px-8 py-8" data-testid="admin-site-settings-page">
      <header className="mb-6">
        <h1 className="font-display text-2xl sm:text-3xl font-bold flex items-center gap-2">
          <Settings className="h-7 w-7 text-[hsl(var(--aito-lagoon))]" />
          Paramètres du site
        </h1>
        <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
          Contrôle du compteur Jeux du Pacifique 2027 et de la page Mémoire (bannière visuelle + bibliothèque).
        </p>
      </header>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="jp2027" data-testid="tab-jp2027"><Flag className="h-4 w-4 mr-1.5" />Jeux du Pacifique 2027</TabsTrigger>
          <TabsTrigger value="memoire" data-testid="tab-memoire"><BookOpen className="h-4 w-4 mr-1.5" />Mémoire du sport</TabsTrigger>
          <TabsTrigger value="admin_contact" data-testid="tab-admin-contact"><Mail className="h-4 w-4 mr-1.5" />Contact admin</TabsTrigger>
        </TabsList>

        {/* JP 2027 */}
        <TabsContent value="jp2027" className="mt-4 space-y-4">
          <Card className="p-5 space-y-4">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <h2 className="font-display font-semibold text-lg">Compteur J-N</h2>
              <div className="text-right">
                <div className="text-xs text-muted-foreground">Aperçu</div>
                <div className="font-mono text-3xl font-bold text-[hsl(var(--aito-gold))]" data-testid="preview-days-left">J-{daysLeft}</div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium">Date d'ouverture (cérémonie)</label>
                <Input
                  type="date"
                  value={settings.jp2027?.opening_date || ""}
                  onChange={(e) => updJp({ opening_date: e.target.value })}
                  data-testid="input-jp-opening-date"
                />
                <p className="text-[10px] text-muted-foreground mt-0.5">Ex : 2027-07-14 (14 juillet 2027)</p>
              </div>
              <div>
                <label className="text-xs font-medium">Date de clôture</label>
                <Input
                  type="date"
                  value={settings.jp2027?.closing_date || ""}
                  onChange={(e) => updJp({ closing_date: e.target.value })}
                  data-testid="input-jp-closing-date"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs font-medium">Titre affiché</label>
                <Input
                  value={settings.jp2027?.label || ""}
                  onChange={(e) => updJp({ label: e.target.value })}
                  placeholder="Jeux du Pacifique 2027"
                  data-testid="input-jp-label"
                />
              </div>
              <div>
                <label className="text-xs font-medium">Lieu</label>
                <Input
                  value={settings.jp2027?.location || ""}
                  onChange={(e) => updJp({ location: e.target.value })}
                  placeholder="Tahiti · Polynésie française"
                  data-testid="input-jp-location"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs font-medium">Texte d'incitation (sidebar Home)</label>
                <Textarea
                  rows={2}
                  value={settings.jp2027?.cta_text || ""}
                  onChange={(e) => updJp({ cta_text: e.target.value })}
                  data-testid="input-jp-cta-text"
                />
              </div>
            </div>

            <div className="flex justify-end">
              <Button onClick={() => save("jp2027")} disabled={saving} className="bg-[hsl(var(--aito-lagoon))] text-black hover:bg-[hsl(var(--aito-lagoon-2))]" data-testid="save-jp2027">
                <Save className="h-4 w-4 mr-1.5" /> {saving ? "Enregistrement…" : "Enregistrer JP 2027"}
              </Button>
            </div>
          </Card>
        </TabsContent>

        {/* Mémoire */}
        <TabsContent value="memoire" className="mt-4 space-y-4">
          {/* Bannière */}
          <Card className="p-5 space-y-4">
            <h2 className="font-display font-semibold text-lg flex items-center gap-2">
              <ImageIcon className="h-5 w-5 text-[hsl(var(--aito-lagoon))]" />
              Bannière visuelle
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <label className="text-xs font-medium">URL de l'image (bannière ~21/9)</label>
                <Input
                  value={settings.memoire?.banner_image_url || ""}
                  onChange={(e) => updMem({ banner_image_url: e.target.value })}
                  placeholder="https://... (image large, format paysage)"
                  data-testid="input-memoire-banner-url"
                />
              </div>
              <div>
                <label className="text-xs font-medium">Titre de la bannière</label>
                <Input
                  value={settings.memoire?.banner_title || ""}
                  onChange={(e) => updMem({ banner_title: e.target.value })}
                  placeholder="L'Histoire des Jeux du Pacifique"
                  data-testid="input-memoire-banner-title"
                />
              </div>
              <div>
                <label className="text-xs font-medium">Sous-titre (facultatif)</label>
                <Input
                  value={settings.memoire?.banner_subtitle || ""}
                  onChange={(e) => updMem({ banner_subtitle: e.target.value })}
                  data-testid="input-memoire-banner-subtitle"
                />
              </div>
            </div>
            {settings.memoire?.banner_image_url && (
              <div className="rounded-lg overflow-hidden border relative aspect-[21/9]" data-testid="preview-memoire-banner">
                <img src={settings.memoire.banner_image_url} alt="preview" className="absolute inset-0 h-full w-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                <div className="absolute bottom-3 left-4 right-4 text-white">
                  <Badge className="bg-[hsl(var(--aito-gold))] text-black text-[10px]">Histoire</Badge>
                  <div className="font-display text-lg font-bold mt-1">{settings.memoire?.banner_title || "L'Histoire des Jeux du Pacifique"}</div>
                  {settings.memoire?.banner_subtitle && <div className="text-sm text-white/90">{settings.memoire.banner_subtitle}</div>}
                </div>
              </div>
            )}
          </Card>

          {/* Livres */}
          <Card className="p-5 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="font-display font-semibold text-lg flex items-center gap-2">
                <Library className="h-5 w-5 text-[hsl(var(--aito-lagoon))]" />
                Livres · Histoire du sport polynésien
              </h2>
              <Button size="sm" variant="outline" onClick={addBook} data-testid="add-book">
                <Plus className="h-4 w-4 mr-1" /> Ajouter un livre
              </Button>
            </div>
            {(settings.memoire?.books || []).length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-6">Aucun livre pour l'instant. Cliquez sur "Ajouter un livre".</p>
            ) : (
              <div className="space-y-4">
                {(settings.memoire?.books || []).map((b, i) => (
                  <Card key={i} className="p-3 grid grid-cols-1 sm:grid-cols-[100px_1fr_auto] gap-3" data-testid={`book-form-${i}`}>
                    {/* Preview cover */}
                    <div className="aspect-[2/3] bg-muted rounded overflow-hidden relative flex items-center justify-center">
                      {b.cover_url ? (
                        <img src={b.cover_url} alt="cover" className="absolute inset-0 h-full w-full object-cover" />
                      ) : (
                        <BookOpen className="h-10 w-10 text-muted-foreground opacity-30" />
                      )}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <Input placeholder="Titre du livre" value={b.title || ""} onChange={(e) => updBook(i, { title: e.target.value })} data-testid={`book-title-${i}`} />
                      <Input placeholder="Auteur" value={b.author || ""} onChange={(e) => updBook(i, { author: e.target.value })} data-testid={`book-author-${i}`} />
                      <Input placeholder="Année" type="number" value={b.year || ""} onChange={(e) => updBook(i, { year: e.target.value })} data-testid={`book-year-${i}`} />
                      <Input placeholder="URL de la couverture" value={b.cover_url || ""} onChange={(e) => updBook(i, { cover_url: e.target.value })} data-testid={`book-cover-${i}`} />
                      <Input className="sm:col-span-2" placeholder="Lien externe (achat / lecture)" value={b.external_url || ""} onChange={(e) => updBook(i, { external_url: e.target.value })} data-testid={`book-url-${i}`} />
                      <Textarea className="sm:col-span-2" rows={2} placeholder="Description courte (facultatif)" value={b.description || ""} onChange={(e) => updBook(i, { description: e.target.value })} data-testid={`book-desc-${i}`} />
                    </div>
                    <div className="flex sm:flex-col gap-2 items-start">
                      {b.external_url && (
                        <a href={b.external_url} target="_blank" rel="noreferrer" title="Ouvrir">
                          <Button size="icon" variant="ghost"><ExternalLink className="h-4 w-4" /></Button>
                        </a>
                      )}
                      <Button size="icon" variant="ghost" onClick={() => removeBook(i)} data-testid={`remove-book-${i}`}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </Card>
                ))}
              </div>
            )}
            <div className="flex justify-end">
              <Button onClick={() => save("memoire")} disabled={saving} className="bg-[hsl(var(--aito-lagoon))] text-black hover:bg-[hsl(var(--aito-lagoon-2))]" data-testid="save-memoire">
                <Save className="h-4 w-4 mr-1.5" /> {saving ? "Enregistrement…" : "Enregistrer Mémoire"}
              </Button>
            </div>
          </Card>
        </TabsContent>

        {/* V12.x — Contact admin (email destinataire de tous les mailto du site) */}
        <TabsContent value="admin_contact" className="mt-4 space-y-4">
          <Card className="p-4 sm:p-6">
            <h2 className="font-display font-bold text-lg mb-1 flex items-center gap-2">
              <Mail className="h-5 w-5 text-[hsl(var(--aito-lagoon))]" /> Email de contact admin
            </h2>
            <p className="text-xs text-muted-foreground mb-4 leading-relaxed">
              Cet email reçoit toutes les demandes utilisateurs affichées dans l'interface :
              modération, accès Admin/COPF, signalements, propositions d'archives « Mémoire du sport »,
              questions de la page Aide, etc. Modifiable ici sans redéploiement.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr] gap-3">
              <div>
                <Label htmlFor="admin-email">Adresse email *</Label>
                <Input
                  id="admin-email"
                  type="email"
                  value={settings.admin_contact?.email || ""}
                  onChange={(e) => updAdmin({ email: e.target.value })}
                  placeholder="anna.yyc@gmail.com"
                  data-testid="admin-contact-email"
                />
              </div>
              <div>
                <Label htmlFor="admin-display">Nom affiché (facultatif)</Label>
                <Input
                  id="admin-display"
                  value={settings.admin_contact?.display_name || ""}
                  onChange={(e) => updAdmin({ display_name: e.target.value })}
                  placeholder="Équipe AITO.PF"
                  data-testid="admin-contact-display"
                />
              </div>
            </div>
            <div className="mt-4 pt-4 border-t flex items-center justify-between">
              <p className="text-xs text-muted-foreground">Astuce : le lien <span className="font-mono">mailto:</span> sera régénéré dans toute l'interface après enregistrement.</p>
              <Button onClick={() => save("admin_contact")} disabled={saving} data-testid="save-admin-contact">
                <Save className="h-4 w-4 mr-1.5" /> Enregistrer
              </Button>
            </div>
          </Card>
        </TabsContent>

      </Tabs>
    </div>
  );
}
